import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';

import NoTableResults from '@shared/components/tables/table-components/no-table-results';

import { LoaderWithTitle } from '@shared/components/loading/loader-with-title';
import { AdvancedFilters } from '@shared/components/advanced-filters';
import { html, nothing } from '@shared/utils/rendering/html';

import {
    EvidenceApproachFilterDefaults,
    EvidenceApproachFilterGroups,
    EvidenceApproachSearchOptions,
} from '../../../../configs/filters/dashboard/evidence-approaches-filters.config';

import { FilterComponents } from './helpers/filter-components';
import { useCachedFetch } from '../../../../utils/hooks/use-cached-fetch';

import withRoleGuard from '@core/utils/routing/with-role-guard';

import Attestations from './attestations';
import ComponentSection from './attestations/component-section';
import InitiateAssessment from './subcomponents/initiate-assessment';
import { MissingPermissionsCard } from './subcomponents/initiate-assessment/fallback';

import withAuthGuard from '@core/utils/routing/with-auth-guard';
import { ClosedPeriod } from './subcomponents/initiate-assessment/closed-period';
import Button from '@shared/components/buttons';
import { Icon, IconNames } from '@shared/components/icons';
import { useFeatureChecks } from '@core/utils/features/feature-context';
import { useAuth } from '@core/utils/context/authentication-context';

const normalizeSearchOption = (searchOption) => {
    const rawValue = searchOption?.value ?? searchOption;

    const normalized = (rawValue ?? 'all').toString().toLowerCase();

    const optionMap = {
        0: 'all',
        all: 'all',
        1: 'id',
        id: 'id',
        2: 'name',
        name: 'name',
        3: 'description',
        description: 'description',
        4: 'criteria id',
        'criteria id': 'criteria id',
        5: 'criteria name',
        'criteria name': 'criteria name',
    };

    return optionMap[normalized] ?? 'all';
};

const GuardedInitiateAssessment = withAuthGuard(
    withRoleGuard(InitiateAssessment, {
        required: ['Admin', 'Capturer', 'Champion'],
        fallback: (props) =>
            html`<${MissingPermissionsCard}
                message=${'You do not have permission to upload evidence.'}
                ...${props}
            />`,
    })
);

const EvidenceComponents = ({ entityAssessment, refresh, isSuccess, filters, isLoading }) => {
    const { useFeature } = useFeatureChecks();
    const { roles } = useAuth();
    const canChampionInitiate = useFeature('legalEntityAssessments.canChampionInitiate', false);
    const filterRef = useRef(undefined);
    const [dataFilters, setDataFilters] = useState({});

    const searchUpdate = useCallback((searchTerm, searchOption) => {
        setDataFilters((prevFilters) => ({
            ...prevFilters,
            searchTerm,
            searchOption: normalizeSearchOption(searchOption),
        }));
    }, []);

    const advancedFiltersUpdate = useCallback((filterData, searchTerm, searchOption) => {
        if (!filterData || Object.keys(filterData).length === 0) {
            setDataFilters({});
            return;
        }

        const transformedFilters = {};

        Object.entries(filterData).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                transformedFilters[key] = value.map((option) => option.value);
            } else if (value && typeof value === 'object' && value.value !== undefined) {
                transformedFilters[key] = value.value;
            } else {
                transformedFilters[key] = value;
            }
        });

        if (searchTerm !== undefined && searchOption !== undefined) {
            transformedFilters.searchTerm = searchTerm;
            transformedFilters.searchOption = normalizeSearchOption(searchOption);
        }

        setDataFilters((prevFilters) => ({
            ...prevFilters,
            ...transformedFilters,
        }));
    }, []);

    const canShowAttestationBody = useMemo(
        () =>
            filters?.legalEntityId &&
            filters?.periodId &&
            isSuccess &&
            entityAssessment?.legalEntityAssessmentId &&
            entityAssessment?.components?.length,
        [filters, isSuccess, entityAssessment]
    );

    const isMissingFilters = useMemo(
        () => !filters?.legalEntityId || !filters?.periodId,
        [filters]
    );

    const isChampionOnly = useMemo(
        () =>
            Array.isArray(roles) &&
            roles.includes('Champion') &&
            !roles.includes('Admin') &&
            !roles.includes('Capturer'),
        [roles]
    );

    const canCreateAssessment = useMemo(
        () =>
            filters?.legalEntityId &&
            filters?.periodId &&
            filters?.periodValid &&
            isSuccess &&
            !entityAssessment?.legalEntityAssessmentId &&
            (!isChampionOnly || canChampionInitiate),
        [filters, isSuccess, entityAssessment, isChampionOnly, canChampionInitiate]
    );

    const isPeriodClosed = useMemo(
        () =>
            !canShowAttestationBody &&
            !filters?.periodValid &&
            isSuccess &&
            !entityAssessment?.legalEntityAssessmentId,
        [filters, isSuccess, entityAssessment, canShowAttestationBody]
    );

    return html` <div class="card">
        <div class="card-header">
            <${AdvancedFilters}
                ref=${filterRef}
                config=${{
                    searchOptions: EvidenceApproachSearchOptions,
                    filterDefaults: EvidenceApproachFilterDefaults,
                    defaultSearchOption: EvidenceApproachFilterDefaults.defaultSearchOption,
                    searchOnly: false,
                }}
                callbacks=${{
                    search: searchUpdate,
                    filter: advancedFiltersUpdate,
                }}
            />
        </div>
        <div class="card-body p-0">
            ${
                isMissingFilters
                    ? html`<${NoTableResults}
                          message=${{ text: 'Please select a Legal Entity and Period above.' }}
                      />`
                    : nothing
            }
            ${
                canCreateAssessment
                    ? html`<${GuardedInitiateAssessment} filters=${filters} />`
                    : nothing
            }
            ${
                canShowAttestationBody
                    ? html` <${Attestations}
                          isSuccess=${isSuccess}
                          components=${entityAssessment.components}
                          filters=${{ ...filters, isSecondPeriod: entityAssessment.isSecondPeriod }}
                          dataFilters=${dataFilters}
                      />`
                    : nothing
            }
            ${isPeriodClosed ? html`<${ClosedPeriod} />` : nothing}

            <${LoaderWithTitle}
                busyLoading=${isLoading}
                title="Fetching Data"
                css="no-table-results"
            />
        </div>
    </div>`;
};

export default EvidenceComponents;
